/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, no-restricted-properties, warp-drive/no-legacy-request-patterns */
// Extends from
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { alias } from '@ember/object/computed';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import Papa from 'papaparse';
import moment from 'moment';
import config from 'linced-app/config/environment';
// Internal Imports
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { conformDate } from '@linc-technologies/linced-common/utils/strings';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class AttendanceController extends Controller {
  // Services
  @service
  dates;

  @service
  features;

  @service
  fetch;

  @service
  permissions;

  @service
  schoolSchema;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  toaster;

  @alias('session.school.jurisdiction')
  jurisdiction;

  PersonType = PersonType;

  Scopes = Scopes;

  @tracked
  currentStep = 0;

  @action
  setCurrentStep(value) {
    this.currentStep = value;
  }

  @tracked
  data = null;

  @tracked
  file = null;

  @action
  setFile(value) {
    this.file = value;
  }

  fileTypes = Object.freeze(['text/csv', '.csv']);

  succeeded = true;

  @tracked
  successfullyProcessed = 0;

  @tracked
  progress = 0;

  @tracked
  startDate = '';

  @tracked
  endDate = '';

  @tracked
  totalNumDays = 0;

  @tracked
  totalRows = 0;

  @tracked
  incorrectSchool = false;

  @tracked
  attendanceExists = false;

  bulkImport = null;

  @tracked
  importing = false;

  @tracked
  skippedNSN = 0;

  @tracked
  skippedNonExistent = 0;

  @tracked
  skippedDays = null;

  @tracked
  batchErrors = null;

  @tracked
  seenHeader = false;

  @tracked
  checkingInProgress = false;

  @tracked
  isEtap = false;

  @tracked
  columns = [];

  validateFile(results) {
    this.columns = results.data[0];
    // Check the file is referencing the correct school
    let importSchoolId = results.data[0][0].trim();
    if (this.isEtap) {
      // Extract school ID from the file name.
      importSchoolId = this.file.name.replace(/.+?_(\d+)\.csv$/, '$1');
    }
    let thisSchoolId = this.get('session.school.jurisdictionalId').trim();
    this.incorrectSchool = importSchoolId !== thisSchoolId;

    let rawStartDate, rawEndDate;
    if (this.isEtap) {
      // Check the first and last data rows to determine the date range.
      let dateIndex = this.columns.indexOf('Date');
      rawStartDate = results.data[1][dateIndex].trim();
      let rowIndex = results.data.length - 1;
      while (isEmpty(rawEndDate) && rowIndex > 0) {
        if (
          !isEmpty(results.data[rowIndex]) &&
          !isEmpty(results.data[rowIndex][dateIndex])
        ) {
          rawEndDate = results.data[rowIndex][dateIndex].trim();
        }
        rowIndex--;
      }
    } else {
      // Check the header row for the start and end dates.
      let startDateIndex = 4;
      let endDateIndex = 5;
      rawStartDate = results.data[0][startDateIndex].trim();
      rawEndDate = results.data[0][endDateIndex].trim();
    }

    // Check if attendance already exists for this timeframe
    let startDate = conformDate(rawStartDate);
    let endDate = conformDate(rawEndDate);
    this.startDate = startDate;
    this.endDate = endDate;
    this.store
      .query('attendance/record', { dateFrom: startDate, dateTo: endDate })
      .then((records) => {
        this.attendanceExists = !isEmpty(findBy(records, 'person.id'));
      })
      .catch(() => {
        this.toaster.error(`Error searching attendance!`);
      })
      .finally(() => {
        this.checkingInProgress = false;
      });
  }

  processEtapRow(input) {
    const dateIndex = this.columns.indexOf('Date');
    const morningIndex = this.columns.indexOf('Morning');
    const afternoonIndex = this.columns.indexOf('Afternoon');
    const commentIndex = this.columns.indexOf('Comment');
    const smsIdIndex = this.columns.indexOf('Pupil ID');

    const smsId = input[smsIdIndex];
    const session = this.session;
    const learner = this.model.learners.find(
      (item) => item.fields['core:smsId']?.value === smsId,
    );

    if (isEmpty(learner)) {
      this.incrementProperty('skippedNonExistent');
      this.incrementProperty('progress');
      return;
    }

    const dateString = conformDate(input[dateIndex]);
    const heroDate = this.dates.getDate(dateString);
    const day = this.dates.dayForDate(dateString);

    if (isEmpty(heroDate) || isEmpty(day)) {
      this.skippedDays.add(dateString);
      this.incrementProperty('progress');
      return;
    }

    let periodIds = day.get('periodIds');
    let attendanceCodes = [input[morningIndex], input[afternoonIndex]];

    attendanceCodes.forEach((code, index) => {
      let comment = '';

      if (!isEmpty(input[commentIndex])) {
        let morningCode = attendanceCodes[0];
        let afternoonCode = attendanceCodes[1];

        // If one is P and one is not P, put note on non-P period
        if (
          (morningCode === 'P' && afternoonCode !== 'P') ||
          (morningCode !== 'P' && afternoonCode === 'P')
        ) {
          // Put note on the non-P code
          if (code !== 'P') {
            comment = input[commentIndex];
          }
        } else {
          // Both P or both non-P: put note on both periods
          comment = input[commentIndex];
        }
      }

      if (!isEmpty(code)) {
        if (!this.data.has(heroDate.date)) {
          this.data.set(heroDate.date, []);
        }
        this.data.get(heroDate.date).push({
          authorId: session.get('person.id'),
          code,
          date: heroDate.date,
          dateId: heroDate.id,
          homeroomId: learner.get('homeroom.id'),
          personId: learner.get('id'),
          periodId: periodIds.objectAt(index),
          schoolId: session.get('activeSchool'),
          notes: comment,
        });
      }
    });
    this.incrementProperty('progress');
  }

  // processRow decodes the attendance string and prepares
  // the two attendance records to upload (1 per period)
  processRow(input) {
    let nsn = input[1];

    // if there is no NSN, skip.
    if (isEmpty(nsn)) {
      this.incrementProperty('skippedNSN');
      this.incrementProperty('progress');
      return;
    }

    // NSNs are automatically padded to 10 digits, so we'll need to do the same to make sure we get a match.
    while (nsn.length < 10) {
      nsn = `0${nsn}`;
    }

    let session = this.session;
    let learner = this.model.learners.find(
      (item) => item.jurisdictionalId === nsn,
    );

    // if the learner doesn't exist, skip them
    if (isEmpty(learner)) {
      this.incrementProperty('skippedNonExistent');
      this.incrementProperty('progress');
      return;
    }
    // Start looping from 9 as that's where attendance data starts
    // Increment by 2 to get to the next day of attendance data
    // CSV format: day,data,nextDay,nextDayData
    for (let i = 9; i < input.length; i += 2) {
      // If attendance for current day doesn't exist, skip it silently
      if (isEmpty(input[i + 1])) {
        continue;
      }
      let dateString = conformDate(input[i]);
      let day = this.dates.dayForDate(dateString);
      let heroDate = this.dates.getDate(dateString);
      if (isEmpty(heroDate) || isEmpty(day)) {
        this.skippedDays.add(dateString);
        continue;
      }
      let periodIds = day.get('periodIds');
      let rawCodes = input[i + 1];
      let codes = [...rawCodes.matchAll(/([A-Za-z?]?)\d{3}/g)].map(
        (match) => match[1] || null,
      );

      if (!this.data.has(heroDate.date)) {
        this.data.set(heroDate.date, []);
      }

      for (let i = 0; i < codes.length; i++) {
        if (!isEmpty(codes[i])) {
          let period = {
            authorId: session.get('person.id'),
            code: codes[i],
            date: heroDate.date,
            dateId: heroDate.id,
            homeroomId: learner.get('homeroom.id'),
            personId: learner.get('id'),
            periodId: periodIds.objectAt(i),
            schoolId: session.get('activeSchool'),
          };
          this.data.get(heroDate.date).push(period);
        }
      }
    }

    // row has now been processed. Increment progress
    this.incrementProperty('progress');
  }

  @action
  handleValidateFile() {
    this.checkingInProgress = true;
    if (isEmpty(this.file)) {
      this.checkingInProgress = false;
      return;
    }

    if (this.file.name.toLowerCase().includes('etap')) {
      this.isEtap = true;
    }

    Papa.parse(this.file.data, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        this.validateFile(results);
        this.totalRows = results.data.length - 1;
      },
    });
  }

  @action
  import() {
    // Initialise import properties and flag that we're starting.
    this.skippedDays = new Set();
    this.data = new Map();
    this.importing = true;

    // Parse with headers to determine number of rows, for progress calc.
    Papa.parse(this.file.data, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        this.totalRows = results.data.length;
      },
    });

    // Parse the file in full, actually processing each row as we go.
    Papa.parse(this.file.data, {
      header: false,
      skipEmptyLines: true,
      step: this.mapRow.bind(this),
      complete: this.importMappedData.bind(this),
    });
  }

  @action
  reset() {
    this.currentStep = 0;
    this.file = null;
    this.progress = 0;
    this.totalRows = 0;
    this.skippedNSN = 0;
    this.skippedNonExistent = 0;
    this.successfullyProcessed = 0;
    this.incorrectSchool = false;
    this.seenHeader = false;
    this.skippedDays = null;
    this.data = null;
    this.startDate = '';
    this.endDate = '';
  }

  // mapRow takes a row of the uploaded CSV & formats it
  mapRow(row) {
    // Skip header row.
    if (!this.seenHeader) {
      this.seenHeader = true;
      return;
    }

    if (this.isEtap) {
      this.processEtapRow(row.data);
    } else {
      this.processRow(row.data);
    }
  }

  // importMappedData sends the mapped import data to the back end for
  // processing.
  importMappedData() {
    if (this.data.size > 0) {
      this.successfullyProcessed = 0;
      this.totalNumDays = this.data.size;
      this.batchErrors = [];
      // Upload in weekly batches to avoid timeouts and make errors granular.
      let promiseChain = Promise.resolve();
      let batchStart = moment(this.startDate, 'YYYY-MM-DD');
      let endDate = moment(this.endDate, 'YYYY-MM-DD');
      let batch = [];
      let batchDays = 0;
      for (
        let date = moment(batchStart);
        date.isSameOrBefore(endDate, 'day');
        date.add(1, 'd')
      ) {
        let dateKey = date.format('YYYY-MM-DD');
        if (this.data.has(dateKey)) {
          batchDays++;
          batch.push(...this.data.get(dateKey));
        }
        if (
          (date.diff(batchStart, 'days') >= 7 || date.isSame(endDate, 'day')) &&
          !isEmpty(batch)
        ) {
          // We're done with this batch; send it.
          // Grab/use loop-local variables to avoid promise issues.
          let startDate = batchStart.format('DD/MM/YYYY');
          let endDate = moment(date).format('DD/MM/YYYY');
          let batchSize = batchDays;
          let options = {
            data: JSON.stringify({ records: batch }),
          };

          promiseChain = promiseChain.then(() => {
            return this.fetch
              .post(
                `${config.APP.apiHost}/attendance/${config.APP.apiPath}/records/:bulk`,
                options,
              )
              .catch(() => {
                this.toaster.error(`Error uploading attendance batch!`);
                // this.batchErrors is a @tracked field read directly via
                // {{#each}} in the template - rebuild-and-reassign (not
                // pushObject, which was already throwing today: batchErrors
                // is set to a plain [] in importMappedData(), above).
                this.batchErrors = [
                  ...this.batchErrors,
                  { startDate, endDate },
                ];
              })
              .finally(() => {
                // Increment by the number of days, not number of records.
                this.incrementProperty('successfullyProcessed', batchSize);
              });
          });
          // Reset for next chunk.
          batch = [];
          batchStart = moment(date).add(1, 'd');
          batchDays = 0;
        }
      }

      promiseChain.finally(() => {
        this.importing = false;
      });
    } else {
      this.importing = false;
    }
  }
}
